import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
} from "@angular/core";
import { MatDialogRef } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { PDFSource, PdfViewerComponent } from "ng2-pdf-viewer";
import { AccountService } from "../../services/account.service";
import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";

enum ZoomScale {
  PageWidth = "page-width",
  PageFit = "page-fit",
  PageHeight = "page-height",
}

@Component({
  selector: "up-pdf-container",
  templateUrl: "./pdf-container.component.html",
  styleUrls: ["./pdf-container.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PdfContainerComponent implements OnInit {
  pdfSource?: PDFSource;
  pdfZoom: number = 1.0;
  totalPages: number = 0;
  isFullScreen: boolean = false;
  isMobile: boolean = false;

  @ViewChild("elementFullScreen", { static: false })
  elementFullScreen!: ElementRef;

  zoomScaleOptions: ZoomScale[] = [
    ZoomScale.PageWidth,
    ZoomScale.PageFit,
    ZoomScale.PageHeight,
  ];

  currentZoomScaleIndex: number = 0;

  @Input() url?: string;
  @Input() canDownload?: boolean;
  @Input() fileName?: string;
  @ViewChild("pdfViewer", { static: false }) pdfViewer?: PdfViewerComponent;

  constructor(
    private _dialogRef: MatDialogRef<PdfContainerComponent>,
    private readonly _accountService: AccountService,
    private readonly _snackBar: MatSnackBar,
    private readonly breakpointObserver: BreakpointObserver,
  ) {}

  ngOnInit(): void {
    const account = this._accountService.selectedAccount;

    this.pdfSource = {
      url: this.url,
      httpHeaders: {
        "X-Account-Domain": account?.domain,
        "X-Account-Id": account?.id,
        Authorization: `Bearer ${this._accountService.getToken()}`,
      },
      withCredentials: true,
    };

    this.breakpointObserver
      .observe([Breakpoints.HandsetPortrait, Breakpoints.HandsetLandscape])
      .subscribe((result) => {
        this.isMobile = result.matches;
      });

    this.zoomOut();
  }

  async download(): Promise<void> {
    try {
      let fileLink = document.createElement("a");

      fileLink.href = this.url!;
      fileLink.download = `${this.fileName}.pdf`;
      fileLink.click();
    } catch (error) {}
  }

  zoomIn() {
    this.pdfZoom += 0.1;
  }

  zoomOut() {
    this.pdfZoom -= 0.1;
  }

  zoomScale() {
    if (this.pdfViewer) {
      this.currentZoomScaleIndex =
        (this.currentZoomScaleIndex + 1) % this.zoomScaleOptions.length;
      this.pdfZoom = 1;
    }
  }

  getZoomScaleTooltip(): string {
    switch (this.currentZoomScaleIndex) {
      case 0:
        return "Ajustar para largura da página";
      case 1:
        return "Ajustar à página";
      case 2:
        return "Ajustar para altura da página";
      default:
        return "";
    }
  }

  close() {
    this.isFullScreen ? this.closeFullScreen() : this._dialogRef.close();
  }

  nextPage() {
    if (this.pdfViewer) this.pdfViewer.pdfViewer.nextPage();
  }

  prevPage() {
    if (this.pdfViewer) this.pdfViewer.pdfViewer.previousPage();
  }

  goToPage(value: any) {
    let pageNumber = parseInt(value.srcElement.value);

    if (this.pdfViewer)
      if (pageNumber < 1 || pageNumber > this.pdfViewer.pdfViewer.pagesCount)
        this._snackBar.open("Página não encontrada.");
      else this.pdfViewer.pdfViewer.currentPageNumber = pageNumber;
  }

  getFullScreenToolTip(): string {
    return this.isFullScreen ? "Sair da Tela Cheia" : "Tela cheia";
  }

  viewInFullScreen(): void {
    if (!this.isFullScreen) this.setFullScreen();
    else this.closeFullScreen();
  }

  setFullScreen() {
    setTimeout(() => {
      const element = this.elementFullScreen.nativeElement;

      if (element.requestFullscreen) element.requestFullscreen();
      else if (element.mozRequestFullScreen) element.mozRequestFullScreen();
      else if (element.webkitRequestFullscreen)
        element.webkitRequestFullscreen();
      else if (element.msRequestFullscreen) element.msRequestFullscreen();

      this.isFullScreen = true;
    }, 1);
  }

  closeFullScreen() {
    document.exitFullscreen();
    window.document.exitFullscreen();
    this.isFullScreen = false;
  }

  @HostListener("fullscreenchange")
  onFullScreenChange() {
    let itsStuckInCornerOfScreen =
      window.screenTop <= 0 &&
      window.screenY <= 0 &&
      document.fullscreenElement === null;
    let itsSeparateInCornerOfScreen =
      window.screenTop > 0 &&
      window.screenY > 0 &&
      document.fullscreenElement === null;

    // Essa condição serve para verificar se está em tela cheia
    if (itsStuckInCornerOfScreen || itsSeparateInCornerOfScreen)
      this.isFullScreen = false;
    else this.isFullScreen = true;
  }

  @HostListener("document:keydown.F11")
  onF11KeyDown() {
    this.viewInFullScreen();
  }
}
